import type { ScaffoldFirstInference } from '@volley/crucible/core/index.js';
import { isScaffoldFirstInference } from './ScaffoldFirstGenreChip';

/**
 * Live-preview scaffold-first (A2.3 web durability) — a per-game client cache of
 * the inferred-genre outcome, so the preview chip survives a refresh / re-nav on
 * the crucible-web SPA (where the desktop's durable `crucible.json` read via
 * `game:status` isn't available over the cloud JSON-RPC).
 *
 * `scaffoldFirst` is an IMMUTABLE create-time hint the client already received
 * on the `game:create` result — caching the value the SPA already has is the
 * right model for it (no per-mount server round-trip to re-fetch a value that
 * never changes). NewGame writes it at create; the Workspace reads it on mount
 * as a durable fallback to the route-state flash.
 *
 * Per-browser (localStorage) — survives refresh / re-nav / browser restart on
 * the creating browser. A different browser/device won't have it; for a
 * create-session affordance shown to the creator that's the right scope (it
 * mirrors desktop's per-machine durability). Cross-device source-of-truth would
 * need a cloud per-game read; deliberately not done — see the chip docs.
 *
 * British spelling throughout (behaviour, colour, etc.).
 */

const KEY_PREFIX = 'crucible.scaffoldFirst.';

/**
 * Per-game cache of the ORIGINAL create prompt (live-preview chip "change"
 * action). The preview genre chip's "change" affordance starts a fresh game
 * from a revised description — landing the user back on New Game with their
 * original prompt pre-filled rather than a blank box. Like `scaffoldFirst`
 * (above), the create prompt is an IMMUTABLE create-time value the client
 * already has, so caching it per-game is the right model (no server round-trip;
 * works on both surfaces; survives a refresh on the creating browser). Absent /
 * garbage → the "change" nav simply lands on a blank New Game form.
 */
const CREATE_PROMPT_KEY_PREFIX = 'crucible.createPrompt.';

/** Best-effort access to `localStorage` (absent under SSR / privacy modes). */
function getStore(): Storage | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null;
  } catch {
    // Accessing `localStorage` can throw under strict privacy settings.
    return null;
  }
}

/**
 * Persist the inferred-genre outcome for a game (called at create time). Silent
 * best-effort: a missing store / quota / serialise failure is swallowed — the
 * chip just falls back to the route-state flash for that session.
 */
export function persistScaffoldFirst(
  gameId: string | undefined,
  inference: ScaffoldFirstInference,
): void {
  const store = getStore();
  if (!store || !gameId) return;
  try {
    store.setItem(KEY_PREFIX + gameId, JSON.stringify(inference));
  } catch {
    /* quota exceeded / denied — best-effort, never throw into the create flow */
  }
}

/**
 * Read the persisted inferred-genre outcome for a game (called on Workspace
 * mount). Returns `null` when absent, the store is unavailable, the payload is
 * unparseable, or it fails validation — so a stale/garbage entry can never
 * render a broken chip (mirrors the route-state boundary guard).
 */
export function readPersistedScaffoldFirst(
  gameId: string | undefined,
): ScaffoldFirstInference | null {
  const store = getStore();
  if (!store || !gameId) return null;
  let raw: string | null;
  try {
    raw = store.getItem(KEY_PREFIX + gameId);
  } catch {
    return null;
  }
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return isScaffoldFirstInference(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Pick the inferred-genre outcome for the preview chip from its sources, with
 * the right FRESHNESS rule (pure — unit-testable without mounting the Workspace).
 *
 * The localStorage cache is consulted ONLY when the `game:status` query has
 * ERRORED — i.e. the cloud RPC doesn't serve it (crucible-web). While
 * `game:status` is LOADING or has RESOLVED (desktop reads the on-disk
 * `crucible.json`), it is AUTHORITATIVE and the cache is NEVER used: a stale
 * cache must not flicker over an authoritative on-disk value/absence, not even
 * during the desktop load window (`statusErrored` keyed on the query's error
 * state, not merely "data is undefined", so loading ≠ web-unserved).
 *
 *  - errored (web)      → route-state flash, then the durable cache.
 *  - loading / resolved → the status value (or the current-session route-state
 *    flash while it loads); the cache is ignored.
 */
export function pickDurableGenreInference(args: {
  readonly statusErrored: boolean;
  readonly fromStatus: ScaffoldFirstInference | null;
  readonly fromRouteState: ScaffoldFirstInference | null;
  readonly fromCache: ScaffoldFirstInference | null;
}): ScaffoldFirstInference | null {
  // 1. An authoritative `game:status` value ALWAYS wins — checked first so a
  //    value RETAINED through a later refetch error (React Query keeps last-good
  //    `data` while `isError`) can't be overridden by the route-state / cache.
  if (args.fromStatus !== null) return args.fromStatus;
  // 2. The localStorage cache is the durable fallback ONLY when `game:status`
  //    ERRORED with no value (the cloud RPC doesn't serve it → crucible-web).
  if (args.statusErrored) return args.fromRouteState ?? args.fromCache ?? null;
  // 3. Otherwise — loading, or RESOLVED with no scaffoldFirst (an authoritative
  //    on-disk absence) — only the current-session route-state flash; NEVER the
  //    cache (no stale flicker / no override of an authoritative absence).
  return args.fromRouteState ?? null;
}

/**
 * Cache the original create prompt for a game (called at create time, alongside
 * {@link persistScaffoldFirst}). Silent best-effort; an empty / whitespace-only
 * prompt is never stored (nothing useful to pre-fill from).
 */
export function persistCreatePrompt(gameId: string | undefined, prompt: string): void {
  const store = getStore();
  if (!store || !gameId) return;
  const trimmed = prompt.trim();
  if (trimmed.length === 0) return;
  try {
    store.setItem(CREATE_PROMPT_KEY_PREFIX + gameId, JSON.stringify(trimmed));
  } catch {
    /* quota exceeded / denied — best-effort, never throw into the create flow */
  }
}

/**
 * Read the cached create prompt for a game (called by the chip's "change"
 * action to pre-fill New Game). Returns a trimmed non-empty string, or `null`
 * when absent / unavailable / unparseable / not a string — so a stale or
 * garbage entry can never seed the form with junk (mirrors the inference read).
 */
export function readCreatePrompt(gameId: string | undefined): string | null {
  const store = getStore();
  if (!store || !gameId) return null;
  let raw: string | null;
  try {
    raw = store.getItem(CREATE_PROMPT_KEY_PREFIX + gameId);
  } catch {
    return null;
  }
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'string') return null;
    const trimmed = parsed.trim();
    return trimmed.length > 0 ? trimmed : null;
  } catch {
    return null;
  }
}
